import { Checkbox, Flex, Grid, HStack, Tag, TagLabel, Text, useToast } from "@chakra-ui/react";
import { useState } from "react";

import { G1Button } from "@components/G1Button";
import { G1Input } from "@components/G1Input";
import { AsyncConfirmModal } from "@components/Modals/AsyncConfirmModal";
import { DefaultAsyncConfirmModalState } from "@components/Modals/ModalConstants";
import { SeaShellBox } from "@components/SeaShellBox";
import { useTagDictionary } from "@hooks/useTagDictionary";
import { defaultSuccessToast } from "@utils/toast";

export const TagsManagementBox = () => {
    const { tags, createTag, deleteTags, isCreating, isDeleting } = useTagDictionary();
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [newTagName, setNewTagName] = useState("");
    const [modal, setModal] = useState(DefaultAsyncConfirmModalState);
    const toast = useToast();

    const entries = Object.entries(tags);

    const toggleSelect = (key: string) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const handleCreate = async () => {
        const name = newTagName.trim();
        if (name.length === 0) { return; }
        const result = await createTag(name);
        if (result) {
            setNewTagName("");
            toast(defaultSuccessToast(`Catégorie "${name}" créée`));
        }
    };

    const handleDeleteSelected = () => {
        const toDelete = entries.filter(([key]) => selectedKeys.has(key));
        const usedOnes = toDelete.filter(([, tag]) => tag.contactCount !== 0);
        const message = usedOnes.length > 0
            ? `${usedOnes.length > 1 ? "Certaines catégories sont" : "Cette catégorie est"} encore utilisée${usedOnes.length > 1 ? "s" : ""} sur au moins un contact (${usedOnes.map(([, t]) => t.name).join(", ")}). Si vous continuez, ${usedOnes.length > 1 ? "les contacts concernés" : "le contact concerné"} ne ${usedOnes.length > 1 ? "seront" : "sera"} plus rattaché${usedOnes.length > 1 ? "s" : ""} à cette catégorie. Voulez-vous continuer ?`
            : `Êtes-vous sûr de vouloir supprimer ${toDelete.length > 1 ? "ces catégories" : "cette catégorie"} ?`;

        setModal({
            message,
            onClose: async () => setModal(DefaultAsyncConfirmModalState),
            onAccept: async () => {
                const success = await deleteTags(Array.from(selectedKeys));
                if (success) {
                    toast(defaultSuccessToast(toDelete.length > 1 ? "Catégories supprimées" : "Catégorie supprimée"));
                    setSelectedKeys(new Set());
                }
                setModal(DefaultAsyncConfirmModalState);
            },
        });
    };

    return (
        <SeaShellBox component="form" mb={5}>
            <Text width="100%" fontWeight="bold">Mes catégories de contact</Text>

            <HStack mb={4}>
                <G1Input component="form" placeholder="Nouvelle catégorie..." value={newTagName}
                         onChange={(e) => setNewTagName(e.target.value)}
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreate(); } }} />
                <G1Button component="form" variant="ok" title="Créer" isLoading={isCreating} fontSize="lg" onClick={handleCreate} disabled={newTagName.trim().length === 0} />
            </HStack>

            {entries.length > 0 && (
                <Grid templateColumns="repeat(auto-fit, minmax(260px, 1fr))" gap={2}>
                    {entries.map(([key, tag]) => (
                        <Flex key={key} align="center" justify="space-between" py={2} px={3} borderWidth="1px" borderRadius="md" borderColor="CSFBlue.100">
                            <Flex align="center" gap={2}>
                                <Checkbox
                                    isChecked={selectedKeys.has(key)}                                     
                                    size="md"
                                    sx={{
                                        '& .chakra-checkbox__control': {
                                            borderWidth: '2px',
                                            borderColor: 'gray.50',
                                            bg: 'gray.50',
                                            _checked: {
                                                bg: 'blue.500',
                                                borderColor: 'blue.500',
                                            },
                                        },
                                    }}
                                    onChange={() => toggleSelect(key)}
                                />
                                <Tag backgroundColor="transparent" borderWidth="1px" borderColor={tag.color} color={tag.color} borderRadius="xl" height="2.5rem">
                                    <TagLabel>{tag.name}</TagLabel>
                                </Tag>
                            </Flex>
                            <Text fontSize="sm" color="gray.500">
                                {(tag.contactCount === 0) ? "Non utilisée" : "Utilisée"}
                            </Text>
                        </Flex>
                    ))}
                </Grid>
            )}

            {selectedKeys.size > 0 && (
                <Flex justify="flex-end" mt={3}>
                    <G1Button component="form" variant="important" title={`Supprimer ${selectedKeys.size} catégorie${selectedKeys.size > 1 ? "s" : ""}`}
                              fontSize="lg" isLoading={isDeleting}
                              onClick={handleDeleteSelected} />
                </Flex>
            )}

            <AsyncConfirmModal message={modal.message} onClose={modal.onClose} onAccept={modal.onAccept} />
        </SeaShellBox>
    );
};